/**
 * Shared paginated read for PostgREST queries (#1919).
 *
 * PostgREST silently caps unpaginated selects at 1,000 rows, so any raw
 * row-fetch that can grow past that (payments, invoices, enrollments) will
 * quietly under-report as a gym grows. This helper loops `.range()` pages
 * until a short page signals the end.
 *
 * Contract for `buildPage(from, to)`:
 *   - Build a FRESH query each call and end it with
 *     `.order(<stable column>, { ascending: true }).range(from, to)`.
 *     A stable order (usually `id`) is required — without it PostgREST may
 *     return overlapping/missing rows across pages.
 *   - Return the awaited-able supabase result (`{ data, error }`).
 *
 * Errors fail the WHOLE read (empty rows + the error) — a partial sum is a
 * silently wrong number, which is worse than an honest failure.
 */

export const SUPABASE_READ_PAGE_SIZE = 1000;

/** Hard stop so a buggy `buildPage` (e.g. missing `.range()` passthrough)
 *  can never loop forever. 500 pages × 1,000 rows is far past any single-gym
 *  dataset; hitting it indicates a bug, so we fail loudly. */
const MAX_PAGES = 500;

export interface PageResult<T> {
  data: T[] | null;
  error: { message: string } | null;
}

export async function fetchAllPages<T>(
  buildPage: (from: number, to: number) => PromiseLike<PageResult<T>>,
  pageSize: number = SUPABASE_READ_PAGE_SIZE,
): Promise<{ rows: T[]; error: Error | null }> {
  if (!(pageSize >= 1)) {
    return { rows: [], error: new Error(`fetchAllPages pageSize must be >= 1, got ${pageSize}`) };
  }

  const rows: T[] = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const from = page * pageSize;
    const { data, error } = await buildPage(from, from + pageSize - 1);
    if (error) {
      return { rows: [], error: new Error(error.message) };
    }
    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < pageSize) {
      return { rows, error: null };
    }
  }
  return {
    rows: [],
    error: new Error(`fetchAllPages exceeded ${MAX_PAGES} pages — check that buildPage applies .range()`),
  };
}
